import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WHICH ANGLE TRACK THIS SESSION IS, and where its siblings are.
 *
 * Extracted 2026-09-09 after the Founder found no track indicator anywhere on
 * the Hooks step. The cause was mine and worth writing down:
 *
 *   MA-3 built `TrackSwitcher` into **v2's** `AutonomousSessionScreen` and, in
 *   the same lane, REMOVED v1's old "Angle 1 of 3" banner — which was broken
 *   (it never advanced) but was at least present. The Founder works in v1:
 *   `/creative-engine/:offerId?session=` renders `CreativeEngineWrapper`, not
 *   the v2 shell. So v1 lost the broken indicator and gained nothing.
 *
 * And his expectation is broader than what MA-3 built: **every** step — Hooks,
 * Script, CTA, Creative Elements, Prompts — should say which track it belongs
 * to and reach the siblings, not only the Angle screen.
 *
 * So the read lives here, in one hook both surfaces call, rather than in one
 * screen's local state. Same discipline as `useAngles` and `useHooks`, for the
 * same reason: two copies of one rule against one database eventually disagree.
 *
 * A FAILED READ IS NOT AN ABSENT SET. The three states are kept apart, because
 * an operator who cannot see track 3 would reasonably conclude it was never
 * created — and this hook is the only thing that would tell them otherwise.
 */
export function useSessionTracks(sessionId, refreshKey = 0) {
  const [state, setState] = useState({ state: 'loading', data: null, error: null });

  const load = useCallback(async () => {
    if (!sessionId) {
      // Not part of anything, and that is a real answer rather than a failure.
      setState({ state: 'ready', data: { tracks: [], track_total: 1 }, error: null });
      return;
    }
    setState((prev) => (prev.state === 'ready' ? prev : { state: 'loading', data: null, error: null }));
    try {
      const res = await axios.get(`${API}/creative/sessions/${sessionId}/tracks`);
      setState({ state: 'ready', data: res.data, error: null });
    } catch (error) {
      setState({ state: 'error', data: null, error: errorText(error) });
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const tracks = state.data?.tracks || [];
  const mine = tracks.find((t) => t.session_id === sessionId) || null;

  return {
    tracks: state,
    reload: load,
    /** This session's own angle — what the steps pass to MA-2's read filter. */
    angleId: mine?.angle_id || null,
    trackIndex: mine?.track_index || null,
    trackTotal: state.data?.track_total ?? null,
    /** True only for a real set. A session of one grows no furniture. */
    isTracked: tracks.length > 1,
  };
}
